import React, { useState } from "react";
import { analyzeGoldenSeries } from "../../api/businessSheet";
import useLocalStorage from "../../hooks/useLocalStorage";
import { normalizeSheetItems } from "../../utils/businessSheetUtils";
import "./styles/GoldenSeriesAnalysis.scss";

const CATEGORIES = [
  { key: "goldenEgg", label: "金のタマゴ", description: "直接の顧客となりうる人や業種" },
  { key: "goldenGoose", label: "金のガチョウ", description: "金のタマゴを産む人や業種" },
  { key: "goldenFarmer", label: "金のファーマー", description: "金のガチョウとの人脈が多い人や業種" },
];

const REQUIRED_COUNT = 3;

const CheckboxGroup = ({ category, candidates, selected, onChange }) => {
  const count = selected.size;

  const handleToggle = (index) => {
    const next = new Set(selected);
    if (next.has(index)) {
      next.delete(index);
    } else if (next.size < REQUIRED_COUNT) {
      next.add(index);
    }
    onChange(next);
  };

  return (
    <div className="golden-series-category">
      <div className="golden-series-category-header">
        <span className="golden-series-category-title">{category.label}</span>
        <span className="golden-series-category-desc">{category.description}</span>
        <span className={`golden-series-count ${count === REQUIRED_COUNT ? "complete" : ""}`}>
          {count}/{REQUIRED_COUNT}
        </span>
      </div>
      <div className="golden-series-items">
        {candidates.map((item, index) => {
          const checked = selected.has(index);
          const disabled = !checked && count >= REQUIRED_COUNT;
          return (
            <label
              key={index}
              className={`golden-series-item${checked ? " checked" : ""}${disabled ? " disabled" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => handleToggle(index)}
              />
              {item}
            </label>
          );
        })}
      </div>
    </div>
  );
};

const GoldenSeriesAnalysis = ({ onConfirm, initialValues }) => {
  const { getValue, setValue } = useLocalStorage();
  const [industryName, setIndustryName] = useState(() => getValue("gs_industryName") || "");
  const [mainService, setMainService] = useState(() => getValue("gs_mainService") || "");
  const [candidates, setCandidates] = useState(() => getValue("gs_candidates") || null);
  const [selected, setSelected] = useState(() => {
    const saved = getValue("gs_selected");
    if (saved) {
      return {
        goldenEgg: new Set(saved.goldenEgg),
        goldenGoose: new Set(saved.goldenGoose),
        goldenFarmer: new Set(saved.goldenFarmer),
      };
    }
    return { goldenEgg: new Set(), goldenGoose: new Set(), goldenFarmer: new Set() };
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const allSelected = CATEGORIES.every(
    (cat) => selected[cat.key].size === REQUIRED_COUNT
  );

  const handleIndustryNameChange = (e) => {
    setIndustryName(e.target.value);
    setValue("gs_industryName", e.target.value);
  };

  const handleMainServiceChange = (e) => {
    setMainService(e.target.value);
    setValue("gs_mainService", e.target.value);
  };

  const handleSelectedChange = (key, next) => {
    const next2 = { ...selected, [key]: next };
    setSelected(next2);
    setValue("gs_selected", {
      goldenEgg: [...next2.goldenEgg],
      goldenGoose: [...next2.goldenGoose],
      goldenFarmer: [...next2.goldenFarmer],
    });
  };

  const handleAnalyze = async () => {
    if (!industryName.trim() || !mainService.trim()) {
      setError("業種名とサービス内容を入力してください。");
      return;
    }
    setError("");
    setIsLoading(true);
    setCandidates(null);
    setValue("gs_candidates", null);
    const resetSelected = { goldenEgg: new Set(), goldenGoose: new Set(), goldenFarmer: new Set() };
    setSelected(resetSelected);
    setValue("gs_selected", { goldenEgg: [], goldenGoose: [], goldenFarmer: [] });

    const { data, status } = await analyzeGoldenSeries(industryName.trim(), mainService.trim());
    setIsLoading(false);

    if (status !== 200 || !data) {
      setError("AI分析に失敗しました。しばらくしてから再試行してください。");
      return;
    }
    setCandidates(data);
    setValue("gs_candidates", data);
  };

  const handleConfirm = () => {
    if (!allSelected || !candidates) return;
    const pick = (key) =>
      [...selected[key]].sort((a, b) => a - b).map((i) => candidates[key][i]);

    // Issue #91: AI が返すのは「悩みタイトル」のみ。
    // 内容・アプローチは既存値を保持し、ユーザーの手入力に委ねる。
    const toItems = (key) => {
      const titles = pick(key);
      return normalizeSheetItems(initialValues?.[key], "goldenSeries").map(
        (item, i) => ({ ...item, title: titles[i] ?? "" }),
      );
    };

    const result = {
      goldenEgg: toItems("goldenEgg"),
      goldenGoose: toItems("goldenGoose"),
      goldenFarmer: toItems("goldenFarmer"),
    };
    const message = [
      "【金のタマゴ】",
      ...result.goldenEgg.map((v, i) => `${i + 1}. ${v.title}`),
      "",
      "【金のガチョウ】",
      ...result.goldenGoose.map((v, i) => `${i + 1}. ${v.title}`),
      "",
      "【金のファーマー】",
      ...result.goldenFarmer.map((v, i) => `${i + 1}. ${v.title}`),
    ].join("\n");
    alert(`シートに反映しました。\n\n${message}`);
    onConfirm(result);
  };

  return (
    <div className="golden-series-analysis">
      <div className="golden-series-inputs">
        <div className="golden-series-input-group">
          <label>あなたの業種名</label>
          <input
            type="text"
            value={industryName}
            onChange={handleIndustryNameChange}
            placeholder="例: 保険業、IT業、飲食業"
          />
        </div>
        <div className="golden-series-input-group">
          <label>主となるサービス内容</label>
          <input
            type="text"
            value={mainService}
            onChange={handleMainServiceChange}
            placeholder="例: 生命保険の販売・コンサルティング"
          />
        </div>
        {error && <p className="golden-series-error">{error}</p>}
        <button
          className="golden-series-analyze-btn"
          onClick={handleAnalyze}
          disabled={isLoading}
        >
          {isLoading ? "分析中..." : "AIで分析する"}
        </button>
      </div>

      {candidates && (
        <div className="golden-series-results">
          <p className="golden-series-instruction">
            各カテゴリーから3つずつ選択してください
          </p>
          {CATEGORIES.map((cat) => (
            <CheckboxGroup
              key={cat.key}
              category={cat}
              candidates={candidates[cat.key] ?? []}
              selected={selected[cat.key]}
              onChange={(next) => handleSelectedChange(cat.key, next)}
            />
          ))}
          <button
            className="golden-series-confirm-btn"
            onClick={handleConfirm}
            disabled={!allSelected}
          >
            シートに反映する
          </button>
        </div>
      )}
    </div>
  );
};

export default GoldenSeriesAnalysis;
